/**
 * The transport for agency-email verification and the signed-in agent.
 *
 * Three calls, all server-decided: ask for a code, submit one, and read the agent to learn
 * whether a code is needed at all. Asking for a
 * replacement is the same call as asking for the first — the server holds the outstanding
 * code and its cooldown, so it decides whether a given ask warrants a new code, and there
 * is no second route to the mailer that skips that check. This module owns fetch and envelope
 * reading and nothing else — the shapes live in contracts.ts and the user-facing prose in
 * errors.ts.
 *
 * Plain async functions rather than an RTK Query service. The two POSTs are not cacheable
 * reads: each one is an act with a side effect at the server (a code sent, an attempt spent),
 * and the screens call them in direct response to a click. The agent read happens once per
 * sign-in to decide which screen to show, so caching it buys nothing.
 *
 * The code is never checked here. The client is told how long it is and when its deadlines
 * fall, which is all a screen needs; whether a particular code is right is the server's
 * answer, and a client that knew it could be made to accept anything (PORT-DEFENSE).
 *
 * The calls carry the user's bearer token for the web API (see config/apiAuth.ts). A call
 * that cannot get one fails as `Unauthenticated` before it is sent, and so does a 401 the
 * API answers without a code.
 */
import { z } from 'zod';
import { apiAuthorizationHeaders } from '@/config/apiAuth';
import type { AgencyDetails, AgentDetails } from '../registration';
import type {
  AgencyOnFile,
  AgentOnFile,
  AgentProfile,
  CallerProfile,
  LensApiResponse,
  ReceivedChallenge,
  VerificationChallenge,
  VerificationResult,
} from './contracts';
import {
  type ApiFailure,
  isApiFailure,
  messageForCode,
  registrationFailure,
  verificationFailure,
} from './errors';

const VERIFICATION_URL = '/api/v1/agent';

/**
 * Reads whichever shape the API answered a failure with.
 *
 * Failures the caller can act on are promoted to a real 4xx carrying RFC 9457
 * `ProblemDetails`; everything else rides a 200 envelope. Both carry the error code, so
 * this collapses the two and the caller never has to know which arrived. Prose (`detail`,
 * `title`) is deliberately not read — it is not the contract.
 */
async function readError(response: Response): Promise<{ code?: string }> {
  try {
    const body = (await response.json()) as
      | LensApiResponse<unknown>
      | { code?: string; extensions?: { code?: string } };

    if ('error' in body && body.error !== undefined) {
      return { code: body.error.code };
    }

    if ('code' in body && typeof body.code === 'string') {
      return { code: body.code };
    }

    if ('extensions' in body && typeof body.extensions?.code === 'string') {
      return { code: body.extensions.code };
    }

    return {};
  } catch {
    return {};
  }
}

/**
 * Sends a request and unwraps the envelope, or throws the coded failure.
 *
 * @param method The HTTP method.
 * @param url The full API path.
 * @param body The JSON request body, or undefined for a bodiless request.
 * @param isExpected Narrows the payload, so a malformed 200 is classified at the boundary.
 * @param fail Builds the failure thrown, which chooses the text the user is shown.
 */
async function send<T>(
  method: 'GET' | 'POST' | 'PUT',
  url: string,
  body: unknown,
  isExpected: (value: unknown) => value is T,
  fail: (code: ApiFailure['code'], status: number) => ApiFailure = verificationFailure,
): Promise<T> {
  const authorization = await apiAuthorizationHeaders(() =>
    fail('Unauthenticated', 0),
  );

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers: {
        ...authorization,
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
        Accept: 'application/json',
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw fail('TransportFailure', 0);
  }

  if (!response.ok) {
    const { code } = await readError(response);
    throw fail(
      (code ?? (response.status === 401 ? 'Unauthenticated' : 'Unknown')) as ApiFailure['code'],
      response.status,
    );
  }

  // A 200 whose body will not parse, or that is not the envelope, is a contract failure
  // rather than a transport one: classify it here, keeping the real status, instead of
  // letting a SyntaxError or TypeError surface far from the boundary (PORT-ERROR).
  let envelope: LensApiResponse<T>;
  try {
    envelope = (await response.json()) as LensApiResponse<T>;
  } catch {
    throw fail('InvalidResponse', response.status);
  }

  if (envelope === null || typeof envelope !== 'object') {
    throw fail('InvalidResponse', response.status);
  }

  if (!envelope.isSuccess) {
    throw fail(
      (envelope.error?.code ?? 'Unknown') as ApiFailure['code'],
      response.status,
    );
  }

  if (!isExpected(envelope.data)) {
    throw fail('InvalidResponse', response.status);
  }

  return envelope.data;
}

/**
 * Whether a parsed value is a well-formed challenge.
 *
 * The two instants are checked as parseable dates, not merely as strings: the screen turns
 * them straight into countdowns, and an unparseable one would render `NaN` seconds rather
 * than failing where it could be explained.
 */
function isVerificationChallenge(value: unknown): value is VerificationChallenge {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const challenge = value as Partial<VerificationChallenge>;
  return (
    typeof challenge.codeLength === 'number' &&
    Number.isInteger(challenge.codeLength) &&
    challenge.codeLength > 0 &&
    typeof challenge.expiresAt === 'string' &&
    !Number.isNaN(Date.parse(challenge.expiresAt)) &&
    typeof challenge.resendAvailableAt === 'string' &&
    !Number.isNaN(Date.parse(challenge.resendAvailableAt)) &&
    typeof challenge.serverTime === 'string' &&
    !Number.isNaN(Date.parse(challenge.serverTime)) &&
    typeof challenge.codeSent === 'boolean'
  );
}

/**
 * Whether a parsed value is a well-formed result.
 *
 * `verified` must be present and true. A success envelope carrying `false` would be the
 * server contradicting itself, and treating it as a pass is the one mistake this gate
 * cannot afford.
 *
 * `registration` is not checked here: the code is spent by now, so a missing or malformed
 * one must not turn an accepted code into a failure. `verifyCode` fills it in instead.
 */
function isVerificationResult(value: unknown): value is VerificationResult {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as Partial<VerificationResult>).verified === true
  );
}

/** A field on file: kept when it has text, dropped when blank, null or not a string. */
const onFileField = z
  .string()
  .nullish()
  .catch(undefined)
  .transform((value) => value?.trim() || undefined);

/**
 * The registration needs as the server sends them. The response JSON is unchecked at
 * runtime, so it is parsed rather than cast. An unreadable `agentOnFile` or `agencyOnFile`
 * is dropped rather than failing the whole answer: the form then just leaves every field
 * in that section editable.
 */
const registrationNeedsSchema = z.object({
  agency: z.boolean(),
  agent: z.boolean(),
  agentOnFile: z
    .object({
      displayName: onFileField,
      titleRank: onFileField,
      role: onFileField,
      phone: onFileField,
      street: onFileField,
      city: onFileField,
      state: onFileField,
      zip: onFileField,
      country: onFileField,
    })
    .nullish()
    .catch(undefined)
    .transform((onFile) => onFile ?? undefined),
  agencyOnFile: z
    .object({
      name: onFileField,
      street: onFileField,
      city: onFileField,
      state: onFileField,
      zip: onFileField,
      country: onFileField,
      phone: onFileField,
      jurisdiction: onFileField,
    })
    .nullish()
    .catch(undefined)
    .transform((onFile) => onFile ?? undefined),
}).transform(({ agency, agent, agentOnFile, agencyOnFile }) => ({
  agency,
  agent,
  ...(agentOnFile && { agentOnFile }),
  ...(agencyOnFile && { agencyOnFile }),
}));

/**
 * The post-OTP registration needs, or every section when they cannot be read. An accepted
 * code is already spent; sign-in reads instead reject malformed registration needs.
 */
function readRegistrationNeeds(value: unknown): VerificationResult['registration'] {
  const parsed = registrationNeedsSchema.safeParse(value);
  if (!parsed.success) {
    return { agency: true, agent: true };
  }

  return parsed.data;
}

/** Asks for a code to be sent to an official agency address.
 *
 * The same call asks for the first code and for every one after it. The server holds the
 * outstanding code and its cooldown, so it is the one that knows which ask this is. An ask
 * inside the cooldown still resolves: the challenge that comes back is the live one, with
 * `codeSent` false. Only a request the server could not read fails.
 *
 * The clock is read here, once, as the answer arrives. The challenge carries that reading
 * with it so the screen's correction is measured against the same moment the server stamped
 * its own time for, rather than against whenever a render happens to run.
 */
export async function requestVerificationCode(email: string): Promise<ReceivedChallenge> {
  const challenge = await send('POST', `${VERIFICATION_URL}/otp`, { email }, isVerificationChallenge);

  return { ...challenge, receivedAt: Date.now() };
}

/**
 * Submits a code for checking. Resolves only when the server accepted it, with which
 * registration sections the caller still has to complete.
 */
export async function verifyCode(email: string, code: string): Promise<VerificationResult> {
  const result = await send(
    'POST',
    `${VERIFICATION_URL}/otp/verify`,
    { email, code },
    isVerificationResult,
  );

  return { verified: true, registration: readRegistrationNeeds(result.registration) };
}

/** Whether a parsed value is the saved agency, which must at least carry its id. */
function isSavedAgency(value: unknown): value is AgencyOnFile & { agencyId: string } {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as AgencyOnFile).agencyId === 'string' &&
    (value as AgencyOnFile).agencyId !== ''
  );
}

/**
 * Saves the agency section of the registration step, resolving with the agency as ARS then
 * holds it.
 *
 * Fills in the agency fields ARS lacks. The server finds the agency from the caller's verified
 * email domain, which is why no domain or agency id is sent.
 */
export function saveAgency(details: AgencyDetails): Promise<AgencyOnFile> {
  const { name, street, city, state, zip, country, phone, jurisdiction } = details;

  return send(
    'PUT',
    `${VERIFICATION_URL}/me/agency`,
    { name, street, city, state, zip, country, phone, jurisdiction },
    isSavedAgency,
    registrationFailure,
  );
}

/**
 * Saves editable agent details to the caller's own agent profile,
 * resolving with the agent as ARS then holds it.
 *
 * Every field is sent; the server writes only those that differ from what ARS holds, and
 * resolves the agent from the caller's token, so no agent id is sent.
 */
export async function saveAgent(details: AgentDetails): Promise<AgentOnFile> {
  const { displayName, titleRank, role, phone, street, city, state, zip, country } = details;

  const saved = await send(
    'PUT',
    `${VERIFICATION_URL}/me`,
    { displayName, titleRank, role, phone, street, city, state, zip, country },
    (value): value is z.input<typeof savedAgentSchema> => savedAgentSchema.safeParse(value).success,
    registrationFailure,
  );
  return savedAgentSchema.parse(saved);
}

/**
 * Whether a parsed value is a well-formed agent.
 *
 * `requiresOtpVerification` must be a boolean. A missing verdict is not read as "not
 * required": that would let the gate open on a response that said nothing.
 */
function isAgentProfile(value: unknown): value is AgentProfile {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as Partial<AgentProfile>).requiresOtpVerification === 'boolean'
  );
}

const profileText = z.string().nullish();
const profileField = profileText.transform((value) => value?.trim() || undefined);
const callerProfileSchema = z.object({
  agentId: z.string().trim().min(1),
  agencyId: profileField,
  displayName: profileField,
  titleRank: profileField,
  role: profileField,
  phone: profileField,
  street: profileField,
  city: profileField,
  state: profileField,
  zip: profileField,
  country: profileField,
  identityEmail: profileField,
  requiresOtpVerification: z.boolean(),
});

const savedAgentSchema = callerProfileSchema.omit({ identityEmail: true, requiresOtpVerification: true });

const agencyProfileSchema = z.object({
  agencyId: z.string().trim().min(1),
  name: profileField,
  street: profileField,
  city: profileField,
  state: profileField,
  zip: profileField,
  country: profileField,
  phone: profileField,
  jurisdiction: profileField,
});

/** Reads the caller's agency; only an explicit NotFound means no agency is registered. */
export async function getCallerAgency(): Promise<AgencyOnFile | null> {
  try {
    const agency = await send(
      'GET',
      `${VERIFICATION_URL}/me/agency`,
      undefined,
      (value): value is z.input<typeof agencyProfileSchema> =>
        agencyProfileSchema.safeParse(value).success,
    );
    return agencyProfileSchema.parse(agency);
  } catch (error) {
    if (isApiFailure(error) && error.code === 'NotFound') return null;
    throw error;
  }
}

/** Reads agent and agency independently, without a registration or update. */
export async function getCallerProfile(): Promise<CallerProfile> {
  const [identity, agency] = await Promise.all([getAgent(false), getCallerAgency()]);
  const parsed = callerProfileSchema.safeParse(identity);
  if (!parsed.success) throw verificationFailure('InvalidResponse', 200);
  return { identity, agent: parsed.data, agency };
}

/** Reads the signed-in agent; profile loading opts out of the sign-in registration check. */
export function getAgent(includeRegistration = true): Promise<AgentProfile> {
  const query = includeRegistration ? '' : '?includeRegistration=false';
  return send('GET', `${VERIFICATION_URL}/me${query}`, undefined, isAgentProfile);
}

/** Where the signed-in user stands at the government-identity gate. */
export type AgentGateCheck =
  | { status: 'required' }
  | { status: 'registration'; email: string; needs: VerificationResult['registration'] }
  | { status: 'verified' }
  /** The API turned the user's token away: only signing in again can help. */
  | { status: 'unauthenticated'; message: string }
  /** The answer could not be read (throttled, down, unreachable, malformed): retrying may help. */
  | { status: 'unavailable'; message: string };

/**
 * Reads whether the signed-in user needs verification or profile completion before entering
 * the portal. A previously verified caller still has to complete missing required fields.
 *
 * Only a well-formed answer opens or closes the gate. A failure is reported as what it is
 * rather than as "a passcode is required", so an outage does not send a verified user
 * through the flow again. Every failure still keeps the portal closed: neither failure
 * status lets the screens behind the gate mount.
 */
export async function checkAgentGate(): Promise<AgentGateCheck> {
  try {
    const agent = await getAgent();
    if (agent.requiresOtpVerification) return { status: 'required' };

    const email = z.string().trim().min(1).safeParse(agent.identityEmail);
    const registration = registrationNeedsSchema.safeParse(agent.registration);
    if (!email.success || !registration.success) {
      throw verificationFailure('InvalidResponse', 200);
    }

    const needs = registration.data;
    return needs.agency || needs.agent
      ? { status: 'registration', email: email.data, needs }
      : { status: 'verified' };
  } catch (error) {
    const failure = isApiFailure(error) ? error : verificationFailure('Unknown', 0);
    if (failure.code === 'Unauthenticated' || failure.status === 401) {
      return { status: 'unauthenticated', message: messageForCode('Unauthenticated') };
    }

    return { status: 'unavailable', message: failure.message };
  }
}

export { isApiFailure };
export type { ApiFailure };
